// @givmo/board — a written consent that approves minutes (CBG-RC9, D-CBG-22): the tolerant reads.
//
// ══ WHAT THE WIRE CARRIES, AND WHY EVERY READ HERE IS OPTIONAL ══════════════════════════════════
//
// RC9 rides reads that already exist, each gaining an OPTIONAL field (contract
// `board-minutes-by-consent.md` §8):
//
//   * the officer's consent read: each set's minutes — their meeting, title and date, the live
//     version's pin (`content_hash`, null while a draft) and, once executed, the outcome and the
//     backend's sentence for it;
//   * the director's consent read: the same minutes, plus `would_approve` and
//     `would_not_approve_reason` — COMPUTED on every read: what the execution would record if the
//     last signature landed now;
//   * the minutes read: `approved_by_written_consent_id` and `approval_consents` (newest first);
//   * the record read: `approved_by_written_consent_id`;
//   * the director's record read: the fingerprint of the bytes it serves (`content_hash`, or
//     `pending_content_hash` while they are not yet the record; CBG-FIX38).
//
// Each decode here never throws and never invents: a field the read did not carry is `null`, and an
// outcome or reason code this build does not know is KEPT, so the page words it with its fallback
// rather than breaking. A read from a backend without RC9 carries none of the fields, and every
// surface then shows exactly what it showed before.
//
// CBG-RC9-MULTI (contract §M9): one consent may name a meeting's minutes AND its closed sessions'.
// The consent reads add `approves_minutes_sets` (every set, in the consent's order, each with its own
// pin, outcome and, for a director, `would_approve`); the minutes read adds
// `approval_consents[].also_names` and `approval_way.consent_names_with` (the other sets beside these).
//
// CBG-RC9-MULTI-CONTRACT (§M12): the list is the ONLY thing read for the sets a consent names. The
// backend still serves `approves_minutes` (its first set) for the readers that haven't moved, but no
// page here reads it, so it can leave the wire in CBG-RC9-MULTI-DROP.

import type {
  BoardMinutesOut,
  OrgBoardDirectorConsentOut,
  OrgBoardDirectorRecordOut,
  OrgBoardRecordOut,
  OrgBoardWrittenConsentOut,
} from '@givmo/api-client';

/** A non-empty string off the wire, or null. */
function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * CBG-RC11 (§A7): the closed session whose OWN minutes a consent names — its id (the minutes page's
 * scope, and the director's way to open them) and its general purpose, the one public fact about it.
 */
export interface ConsentClosedSession {
  segmentId: string;
  purposeLabel: string | null;
}

function closedSessionOf(raw: Record<string, unknown>): ConsentClosedSession | null {
  const session = raw.closed_session;
  if (!isRecord(session)) return null;
  const segmentId = text(session.segment_id);
  if (segmentId === null) return null;
  return { segmentId, purposeLabel: text(session.purpose_label) };
}

/**
 * CBG-RC9-MULTI (§M9): another set of minutes named beside these — its record, and its closed session
 * when it is one's own minutes (`null` for the meeting's own).
 */
export interface NamedSet {
  recordId: string;
  closedSession: ConsentClosedSession | null;
}

/** A list of named sets off the wire, in its order; anything unreadable is skipped. Never throws. */
export function namedSets(value: unknown): NamedSet[] {
  if (!Array.isArray(value)) return [];
  const sets: NamedSet[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) continue;
    const recordId = text(entry.record_id);
    if (recordId === null || sets.some((set) => set.recordId === recordId)) continue;
    sets.push({ recordId, closedSession: closedSessionOf(entry) });
  }
  return sets;
}

/** The minutes a consent names, as the officer's read carries them. */
export interface ConsentMinutes {
  recordId: string;
  /** Their meeting: the address of the minutes page. Null once nothing is left to approve. */
  meetingId: string | null;
  minutesTitle: string | null;
  /** The minutes' own date, `YYYY-MM-DD`. */
  meetingDate: string | null;
  /** The live version's pin: the fingerprint its text names. Null while a draft. */
  contentHash: string | null;
  /** What the execution recorded: `approved`, or why it approved nothing. Null until executed. */
  outcome: string | null;
  /** The backend's own sentence for the outcome. */
  outcomeStatement: string | null;
  /** A closed session's own minutes (CBG-RC11), else null: the meeting's own. */
  closedSession: ConsentClosedSession | null;
}

/**
 * CBG-RC9-MULTI (§M9): every set the officer's consent read names, in the consent's order, off
 * `approves_minutes_sets` alone (CBG-RC9-MULTI-CONTRACT). Empty for a consent that names no minutes;
 * the first is what a one-set page calls "the minutes".
 */
export function consentMinutesSets(
  consent: Pick<OrgBoardWrittenConsentOut, 'approves_minutes_sets'> | null | undefined,
): ConsentMinutes[] {
  const listed: unknown = consent?.approves_minutes_sets;
  if (!Array.isArray(listed)) return [];
  return listed.flatMap((entry) => {
    const set = officerSetOf(entry);
    return set === null ? [] : [set];
  });
}

function officerSetOf(raw: unknown): ConsentMinutes | null {
  if (!isRecord(raw)) return null;
  const recordId = text(raw.record_id);
  if (recordId === null) return null;
  return {
    recordId,
    meetingId: text(raw.meeting_id),
    minutesTitle: text(raw.minutes_title),
    meetingDate: text(raw.meeting_date),
    contentHash: text(raw.content_hash),
    outcome: text(raw.outcome),
    outcomeStatement: text(raw.outcome_statement),
    closedSession: closedSessionOf(raw),
  };
}

/** The minutes a consent names, as the director's read carries them. */
export interface DirectorConsentMinutes {
  recordId: string;
  meetingId: string | null;
  minutesTitle: string | null;
  meetingDate: string | null;
  /** The pin: the fingerprint the consent's text names. */
  contentHash: string | null;
  /**
   * Whether the last signature landing NOW would approve them. `null` when the read says nothing a
   * page can stand on (no pin, or a value this build cannot read): then neither the minutes nor a
   * reason is shown.
   */
  wouldApprove: boolean | null;
  /** Why it would not, in the outcome's vocabulary; kept as served, a code this build may not know. */
  wouldNotApproveReason: string | null;
  /** A closed session's own minutes (CBG-RC11), else null. */
  closedSession: ConsentClosedSession | null;
  /**
   * CBG-RC11 (§A8): what the minutes record of THIS reader's own seat — `recused` from a decision in
   * them, or `stepped_out` of a closed session for a conflict — never another seat's name. Anything
   * else reads as neither.
   */
  recordsYourConflict: 'recused' | 'stepped_out' | null;
}

/**
 * CBG-RC9-MULTI (§M9): every set the director's consent read names, in the consent's order, each with
 * what the last signature would do to it and which conflict of the reader's own it records — off
 * `approves_minutes_sets` alone (CBG-RC9-MULTI-CONTRACT). Empty for a consent that names no minutes.
 */
export function directorConsentMinutesSets(
  consent: Pick<OrgBoardDirectorConsentOut, 'approves_minutes_sets'> | null | undefined,
): DirectorConsentMinutes[] {
  const listed: unknown = consent?.approves_minutes_sets;
  if (!Array.isArray(listed)) return [];
  return listed.flatMap((entry) => {
    const set = directorSetOf(entry);
    return set === null ? [] : [set];
  });
}

function directorSetOf(raw: unknown): DirectorConsentMinutes | null {
  if (!isRecord(raw)) return null;
  const recordId = text(raw.record_id);
  if (recordId === null) return null;
  return {
    recordId,
    meetingId: text(raw.meeting_id),
    minutesTitle: text(raw.minutes_title),
    meetingDate: text(raw.meeting_date),
    contentHash: text(raw.content_hash),
    wouldApprove: typeof raw.would_approve === 'boolean' ? raw.would_approve : null,
    wouldNotApproveReason: text(raw.would_not_approve_reason),
    closedSession: closedSessionOf(raw),
    recordsYourConflict:
      raw.records_your_conflict === 'recused' || raw.records_your_conflict === 'stepped_out'
        ? raw.records_your_conflict
        : null,
  };
}

/**
 * Whether the minutes the director's record read serves are the ones a consent names (CBG-FIX38):
 * the read's own fingerprint — `content_hash` once they are the record, `pending_content_hash` while
 * they are not — against the pin the consent's text names.
 *
 * * `same` — the bytes shown are the pinned ones;
 * * `changed` — they are not: the minutes moved after the consent went out, whatever an earlier
 *   consent read said;
 * * `unknown` — the read carries no fingerprint to compare (a backend before the field), so a page
 *   says nothing either way rather than call two absences a match.
 */
export type NamedMinutesMatch = 'same' | 'changed' | 'unknown';

export function namedMinutesMatch(
  record:
    | Pick<OrgBoardDirectorRecordOut, 'content_hash' | 'pending_content_hash'>
    | null
    | undefined,
  pin: string | null | undefined,
): NamedMinutesMatch {
  const served = text(record?.content_hash) ?? text(record?.pending_content_hash);
  const pinned = text(pin);
  if (served === null || pinned === null) return 'unknown';
  return served === pinned ? 'same' : 'changed';
}

/** One consent naming a set of minutes, off the minutes read. */
export interface MinutesApprovalConsent {
  id: string;
  status: string;
  /** What its execution did to THESE minutes (their own set's outcome, CBG-RC9-MULTI). */
  outcome: string | null;
  /** CBG-RC9-MULTI (§M9): the other sets it names, in its order; empty for a consent naming one. */
  alsoNames: NamedSet[];
}

/**
 * Every consent naming these minutes, newest first — or `null` when the read did not carry the list
 * at all (a backend without RC9), which is not the same answer as "none": the page then offers no
 * consent door, rather than one it cannot tell is already out.
 */
export function minutesApprovalConsents(
  minutes: Pick<BoardMinutesOut, 'approval_consents'> | null | undefined,
): MinutesApprovalConsent[] | null {
  const raw: unknown = minutes?.approval_consents;
  if (!Array.isArray(raw)) return null;
  const consents: MinutesApprovalConsent[] = [];
  for (const entry of raw) {
    if (!isRecord(entry)) continue;
    const id = text(entry.id);
    const status = text(entry.status);
    if (id === null || status === null) continue;
    consents.push({
      id,
      status: status.toLowerCase(),
      outcome: text(entry.outcome),
      alsoNames: namedSets(entry.also_names),
    });
  }
  return consents;
}

/**
 * The one line the minutes page says about a consent naming them, if any:
 *
 * * **pending** — one is out for signature (whatever is newer), with the other sets it names
 *   (CBG-RC9-MULTI);
 * * **drafted** — the newest is a draft that hasn't gone to the board;
 * * **refused** — the newest was executed and approved nothing, while the minutes are still not the
 *   record (an approval another way is the approved line's to say, and minutes that are gone have no
 *   page to say it on).
 *
 * A withdrawn consent says nothing: the officer stopped it. An executed one that approved them is the
 * approved line's (`approved_by_written_consent_id`).
 */
export type MinutesConsentLine =
  | { kind: 'pending'; consentId: string; alsoNames: NamedSet[] }
  | { kind: 'drafted'; consentId: string }
  | { kind: 'refused'; consentId: string; outcome: string | null };

export function minutesConsentLine(
  consents: readonly MinutesApprovalConsent[] | null,
  minutesAreTheRecord: boolean,
): MinutesConsentLine | null {
  if (consents === null || consents.length === 0) return null;
  const pending = consents.find((consent) => consent.status === 'pending');
  if (pending !== undefined) {
    return { kind: 'pending', consentId: pending.id, alsoNames: pending.alsoNames };
  }
  const newest = consents[0];
  if (newest === undefined) return null;
  if (newest.status === 'draft') return { kind: 'drafted', consentId: newest.id };
  if (newest.status === 'executed' && newest.outcome !== 'approved' && !minutesAreTheRecord) {
    return { kind: 'refused', consentId: newest.id, outcome: newest.outcome };
  }
  return null;
}

/**
 * The consent whose execution approved these minutes (or this record), when one did — the minutes
 * read's and the record read's `approved_by_written_consent_id`.
 */
export function approvedByConsentId(
  source:
    | Pick<BoardMinutesOut, 'approved_by_written_consent_id'>
    | Pick<OrgBoardRecordOut, 'approved_by_written_consent_id'>
    | null
    | undefined,
): string | null {
  return text(source?.approved_by_written_consent_id);
}

/**
 * The consent's text split around the paragraph that names the minutes by their fingerprint — the
 * one line it cannot go out without (contract §2, Q7) — so a preview can mark it, quietly. `null`
 * when the text does not carry the fingerprint (the officer removed it), and the preview then marks
 * nothing rather than the wrong line.
 */
export function fingerprintParagraph(
  body: string,
  fingerprint: string | null | undefined,
): { before: string; line: string; after: string } | null {
  const hash = (fingerprint ?? '').trim();
  if (hash.length === 0) return null;
  const at = body.indexOf(hash);
  if (at < 0) return null;
  const start = body.lastIndexOf('\n\n', at);
  const from = start < 0 ? 0 : start + 2;
  const end = body.indexOf('\n\n', at);
  const to = end < 0 ? body.length : end;
  return { before: body.slice(0, from), line: body.slice(from, to), after: body.slice(to) };
}
